import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, AuthTokens } from '../types';
import api from '../services/api';
import { clearPersistentApiCache } from '../services/apiCache';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isSupervisor: boolean;
  isCustomer: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const savedUser = localStorage.getItem('user');
    const token = localStorage.getItem('accessToken');
    if (savedUser && token) {
      try {
        setUser(JSON.parse(savedUser));
      } catch { localStorage.clear(); }
    }
    setLoading(false);
  }, []);

  const login = async (username: string, password: string) => {
    try {
      const response = await api.login(username, password);
      setUser(response.user);
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err?.response?.data?.message || err?.message || 'Login failed';
      if (err?.code === 'ERR_NETWORK' || err?.message?.includes('Network Error')) {
        throw new Error('Unable to connect to server. Please ensure the backend is running.');
      }
      throw new Error(msg);
    }
  };

  const logout = () => {
    api.logout();
    // Purge per-user dashboard caches so the next login never sees previous
    // user's scoped payload (e.g. admin payload leaking into BDP session).
    try {
      const ls = window.localStorage;
      const toRemove: string[] = [];
      for (let i = 0; i < ls.length; i++) {
        const k = ls.key(i);
        if (k && k.startsWith('hala.dashboard.overview.cache.')) toRemove.push(k);
      }
      toRemove.forEach(k => ls.removeItem(k));
    } catch { /* ignore */ }
    // Drop the SWR client cache too — same reason.
    try {
      const { invalidateCache } = require('../services/clientCache');
      invalidateCache();
    } catch { /* ignore */ }
    // Drop the persistent IndexedDB API cache so the next user does
    // not see the previous user's scoped responses on first paint.
    // Fire-and-forget — IDB clear is async but the user is being
    // redirected immediately and the data is already wiped from L1.
    try { void clearPersistentApiCache(); } catch { /* ignore */ }
    setUser(null);
    window.location.href = '/login';
  };

  return (
    <AuthContext.Provider value={{
      user, loading, login, logout,
      isAuthenticated: !!user,
      isAdmin: user?.role === 'admin',
      isSupervisor: user?.role === 'supervisor',
      isCustomer: user?.role === 'customer',
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}

export default AuthContext;
